import React, { useState, useEffect } from 'react'
import { 
  Building2, 
  Mail, 
  Phone, 
  Calendar, 
  User, 
  Activity, 
  Settings, 
  ArrowRight, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  UserCheck,
  Download,
  FileText,
  BarChart,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs'
import { Pagination } from '../components/ui/Pagination'
import { WorkflowProgress, WorkflowStepProgress } from '../components/ui/WorkflowProgress'
import { ProfilesService, type DetailedStatus } from '../services/profilesService'
import type { 
  IntelligenceProfile, 
  ProfileStatusFilter, 
  ProfilesQuery,
  ProfilesCounts,
  ProfilesPagination,
  ReportAsset 
} from '../types'

const DEFAULT_PAGE_SIZE = 20

// Enhanced profile type with workflow progress
interface EnhancedProfile extends IntelligenceProfile {
  workflowProgress?: DetailedStatus
  showWorkflowDetails?: boolean
}

export function AdminProfilesPage() {
  const [profiles, setProfiles] = useState<EnhancedProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [executingOrchestrator, setExecutingOrchestrator] = useState<string | null>(null)
  const [justExecutedProfiles, setJustExecutedProfiles] = useState<Set<string>>(new Set())
  const [statusPolling, setStatusPolling] = useState<Record<string, NodeJS.Timeout>>({})
  const [progressStartTimes, setProgressStartTimes] = useState<Record<string, number>>({})
  const [completingProfiles, setCompletingProfiles] = useState<Set<string>>(new Set())
  const [fadingOutProfiles, setFadingOutProfiles] = useState<Set<string>>(new Set())
  
  // Query state
  const [currentPage, setCurrentPage] = useState(1)
  const [activeTab, setActiveTab] = useState<ProfileStatusFilter>('all')
  const [counts, setCounts] = useState<ProfilesCounts>({
    total: 0,
    pending: 0,
    processed: 0,
    assigned: 0
  })
  const [pagination, setPagination] = useState<ProfilesPagination>({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })

  useEffect(() => {
    loadProfiles()
  }, [currentPage, activeTab])

  useEffect(() => {
    // Cleanup polling intervals on unmount
    return () => {
      Object.values(statusPolling).forEach(intervalId => {
        clearInterval(intervalId)
      })
    }
  }, [statusPolling])

  const loadProfiles = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user is authenticated
      const token = localStorage.getItem('stratiqx_access_token')
      if (!token) {
        setError('Authentication required. Please log in to access admin profiles.')
        setLoading(false)
        return
      }
      
      const query: ProfilesQuery = {
        page: currentPage,
        limit: DEFAULT_PAGE_SIZE,
        status: activeTab,
        sortBy: 'created_at',
        sortOrder: 'desc'
      }
      
      const response = await ProfilesService.fetchProfiles(query)
      
      if (response.success) {
        // Fetch assets for profiles with completed processing
        const profilesWithAssets = await Promise.all(
          response.profiles.map(async (profile) => {
            const enhancedProfile: EnhancedProfile = { ...profile }
            
            if (profile.submission?.processing_status === 'completed' || 
                (profile.submission?.processing_status === 'failed' && profile.submission?.response_url)) {
              try {
                const statusResult = await ProfilesService.getStatus(profile.tracking_id)
                if (statusResult.success) {
                  const assets = statusResult.assets || statusResult.data?.assets || []
                  enhancedProfile.submission = profile.submission ? {
                    ...profile.submission,
                    assets: assets
                  } : null
                }
              } catch (error) {
                console.error(`Failed to fetch assets for ${profile.tracking_id}:`, error)
              }
            }
            
            // Fetch detailed workflow progress for processing profiles OR profiles with active polling OR just executed
            if (profile.tracking_id) {
              const hasActivePolling = statusPolling[profile.tracking_id]
              const isProcessing = profile.submission?.processing_status === 'processing'
              const justExecuted = executingOrchestrator === profile.id || justExecutedProfiles.has(profile.id)
              console.log(`Profile ${profile.tracking_id} (id: ${profile.id}): processing=${isProcessing}, polling=${!!hasActivePolling}, executing=${justExecuted}, recentlyExecuted=${justExecutedProfiles.has(profile.id)}`)
              
              if (isProcessing || hasActivePolling || justExecuted) {
                try {
                  console.log(`Fetching detailed status for ${profile.tracking_id}...`)
                  const detailedStatus = await ProfilesService.getDetailedStatus(profile.tracking_id)
                  console.log(`Raw detailed status response for ${profile.tracking_id}:`, detailedStatus)
                  if (detailedStatus.success) {
                    // API now provides clean data with 6-step workflow, use it directly
                    const apiMetrics = detailedStatus.data.processing_metrics || {}
                    const logs = detailedStatus.data.logs || []
                    
                    // Use API metrics directly since data is now clean, ensure completion is properly shown
                    const isCompleted = detailedStatus.status === 'completed'
                    const completedSteps = isCompleted ? 6 : (apiMetrics.completed_steps || 0)
                    
                    // Use API current_step directly, with fallback for legacy data
                    const currentStep = isCompleted ? 'workflow_complete' : (apiMetrics.current_step || 'processing')
                    
                    const metrics = {
                      progress_percentage: isCompleted ? 100 : (apiMetrics.progress_percentage || 0),
                      completed_steps: completedSteps,
                      failed_steps: apiMetrics.failed_steps || 0,
                      total_steps: apiMetrics.total_steps || 6,
                      current_step: currentStep,
                      current_step_number: completedSteps + 1 > 6 ? 6 : completedSteps + 1,
                      processing_time_ms: apiMetrics.processing_time_ms || 0
                    }
                    
                    console.log(`Got workflow progress for ${profile.tracking_id}:`, {
                      status: detailedStatus.status,
                      progress: `${metrics.completed_steps}/${metrics.total_steps}`,
                      percentage: `${metrics.progress_percentage}%`,
                      current_step: metrics.current_step,
                      api_progress: detailedStatus.progress
                    })
                    
                    // Create status object with clean API data
                    const adaptedStatus = {
                      ...detailedStatus,
                      data: {
                        ...detailedStatus.data,
                        processing_metrics: metrics,
                        logs: logs
                      }
                    }
                    enhancedProfile.workflowProgress = adaptedStatus
                    
                    // Remove from justExecuted set if it's completed or failed (keep during processing)
                    if (['completed', 'failed'].includes(detailedStatus.status.toLowerCase())) {
                      setJustExecutedProfiles(prev => {
                        const newSet = new Set(prev)
                        newSet.delete(profile.id)
                        return newSet
                      })
                    }
                  } else {
                    console.log(`No detailed status data for ${profile.tracking_id}:`, detailedStatus.message)
                  }
                } catch (error) {
                  console.error(`Failed to fetch workflow progress for ${profile.tracking_id}:`, error)
                }
              }
            } else {
              console.warn(`Profile ${profile.id} has no tracking_id`)
            }
            
            return enhancedProfile
          })
        )
        
        setProfiles(profilesWithAssets)
        setPagination(response.pagination)
        setCounts(response.counts)
      } else {
        setError(response.error || 'Failed to load profiles')
      }
    } catch (err) {
      console.error('Error loading profiles:', err)
      
      // Check if it's an authentication error
      if (err instanceof Error && err.message.includes('401')) {
        setError('Authentication expired. Please log in again to access admin profiles.')
        // Optionally clear the expired token
        localStorage.removeItem('stratiqx_access_token')
      } else {
        setError('Failed to fetch profiles. Please check your connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab as ProfileStatusFilter)
    setCurrentPage(1) // Reset to first page when changing tabs
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const startDetailedStatusPolling = (trackingId: string) => {
    // Clear existing polling for this tracking ID
    if (statusPolling[trackingId]) {
      clearInterval(statusPolling[trackingId])
    }

    // Start new enhanced polling every 3 seconds
    const intervalId = setInterval(async () => {
      try {
        const detailedStatus = await ProfilesService.getDetailedStatus(trackingId)
        if (detailedStatus.success) {
          // API now provides clean data with 6-step workflow, use directly
          const logs = detailedStatus.data.logs || []
          const apiMetrics = detailedStatus.data.processing_metrics || {}
          
          // Use clean API metrics directly, but ensure completion is properly shown
          const isCompleted = detailedStatus.status === 'completed'
          const completedSteps = isCompleted ? 6 : (apiMetrics.completed_steps || 0)
          
          // Use API current_step directly, with fallback for legacy data
          const currentStep = isCompleted ? 'workflow_complete' : (apiMetrics.current_step || 'processing')
          
          const correctedMetrics = {
            progress_percentage: isCompleted ? 100 : (apiMetrics.progress_percentage || 0),
            completed_steps: completedSteps,
            failed_steps: apiMetrics.failed_steps || 0,
            total_steps: apiMetrics.total_steps || 6,
            current_step: currentStep,
            current_step_number: completedSteps + 1 > 6 ? 6 : completedSteps + 1,
            processing_time_ms: apiMetrics.processing_time_ms || 0
          }

          // Update the profile with corrected data
          setProfiles(prevProfiles => 
            prevProfiles.map(p => 
              p.tracking_id === trackingId 
                ? { 
                    ...p, 
                    workflowProgress: {
                      ...detailedStatus,
                      data: {
                        ...detailedStatus.data,
                        processing_metrics: correctedMetrics,
                        logs: logs
                      }
                    },
                    submission: p.submission 
                      ? { ...p.submission, processing_status: detailedStatus.status }
                      : { processing_status: detailedStatus.status, submitted_at: new Date().toISOString() }
                  }
                : p
            )
          )
          
          // Stop polling if status is completed or failed
          if (['completed', 'failed'].includes(detailedStatus.status.toLowerCase())) {
            console.log(`Workflow ${trackingId} finished with status: ${detailedStatus.status}`)
            clearInterval(intervalId)
            setStatusPolling(prev => {
              const newPolling = { ...prev }
              delete newPolling[trackingId]
              return newPolling
            })
            
            // Mark as completing to show success animation
            const profile = profiles.find(p => p.tracking_id === trackingId)
            if (profile) {
              setCompletingProfiles(prev => new Set([...prev, profile.id]))
            }
            
            // Phase 1: Show completion state with success animation (3 seconds)
            setTimeout(() => {
              if (profile) {
                setFadingOutProfiles(prev => new Set([...prev, profile.id]))
              }
            }, 3000)
            
            // Phase 2: Start fade out animation (1 second)
            setTimeout(() => {
              if (profile) {
                // Clear all states
                setJustExecutedProfiles(prev => {
                  const newSet = new Set(prev)
                  newSet.delete(profile.id)
                  return newSet
                })
                
                setCompletingProfiles(prev => {
                  const newSet = new Set(prev)
                  newSet.delete(profile.id)
                  return newSet
                })
                
                setFadingOutProfiles(prev => {
                  const newSet = new Set(prev)
                  newSet.delete(profile.id)
                  return newSet
                })
                
                setProgressStartTimes(prev => {
                  const newTimes = { ...prev }
                  delete newTimes[profile.id]
                  return newTimes
                })
              }
              
              // Refresh to get final assets
              loadProfiles()
            }, 4000) // 3s completion + 1s fade out
          }
        }
      } catch (error) {
        console.error('Detailed status polling error:', error)
      }
    }, 3000) // Poll every 3 seconds for more responsive updates

    setStatusPolling(prev => ({ ...prev, [trackingId]: intervalId }))
  }

  const handleExecuteOrchestrator = async (profile: IntelligenceProfile) => {
    // Prevent multiple simultaneous executions
    if (executingOrchestrator) {
      console.log('Another orchestrator is already executing, ignoring click')
      return
    }
    
    try {
      setExecutingOrchestrator(profile.id)
      
      // Immediately mark as executed and start showing progress
      console.log('Immediately showing progress for profile:', profile.id)
      setJustExecutedProfiles(prev => new Set([...prev, profile.id]))
      
      // Track when progress started for minimum display time
      setProgressStartTimes(prev => ({ ...prev, [profile.id]: Date.now() }))
      
      // Show immediate progress state while orchestrator starts
      setProfiles(prevProfiles => 
        prevProfiles.map(p => 
          p.id === profile.id
            ? {
                ...p,
                workflowProgress: {
                  success: true,
                  trackingId: profile.tracking_id,
                  status: 'processing',
                  progress: 0,
                  data: {
                    submission: null,
                    assets: [],
                    logs: [],
                    processing_metrics: {
                      progress_percentage: 0,
                      completed_steps: 0,
                      failed_steps: 0,
                      total_steps: 6,
                      current_step: 'workflow_start',
                      current_step_number: 1,
                      processing_time_ms: 0
                    },
                    downloadLinks: []
                  }
                }
              }
            : p
        )
      )
      
      startDetailedStatusPolling(profile.tracking_id)
      // Small delay to let user see the immediate progress
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Then execute the orchestrator (this might be slow)
      const result = await ProfilesService.executeOrchestrator(profile.id, profile.tracking_id)
      
      // Clear executing state immediately after API call completes
      console.log(`Clearing executingOrchestrator for profile ${profile.id}`)
      setExecutingOrchestrator(null)
      
      // Small delay to ensure state update is processed before continuing
      await new Promise(resolve => setTimeout(resolve, 100))
      
      console.log('Orchestrator execution result:', result)
      if (result.success) {
        console.log('Orchestrator execution success:', result.message)
      } else {
        console.error('Failed to execute orchestrator:', result.message)
        // Only remove from executed set if it's not a timeout (orchestrator might still be running)
        if (!result.message.includes('timed out')) {
          setJustExecutedProfiles(prev => {
            const newSet = new Set(prev)
            newSet.delete(profile.id)
            return newSet
          })
        } else {
          console.log('API timed out but orchestrator may still be running - keeping progress visible')
        }
      }
    } catch (err) {
      console.error('Error executing orchestrator:', err)
      // Remove from executed set on error
      setJustExecutedProfiles(prev => {
        const newSet = new Set(prev)
        newSet.delete(profile.id)
        return newSet
      })
      setExecutingOrchestrator(null)
    }
  }

  const handleDownloadAsset = async (asset: ReportAsset) => {
    try {
      // Make authenticated request to download the actual file content
      const response = await ProfilesService.downloadAssetContent(asset.id)
      
      if (response.ok) {
        // Get the file blob from the response
        const blob = await response.blob()
        
        // Create object URL and trigger download
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = asset.file_name || `asset-${asset.id}.${asset.asset_type}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up the object URL
        window.URL.revokeObjectURL(url)
        
        console.log(`✅ Successfully downloaded asset: ${asset.file_name}`)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Download failed' }))
        console.error('Download failed:', errorData)
        alert(`Download failed: ${errorData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error downloading asset:', error)
      alert(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const toggleWorkflowDetails = (profileId: string) => {
    setProfiles(prevProfiles =>
      prevProfiles.map(p =>
        p.id === profileId
          ? { ...p, showWorkflowDetails: !p.showWorkflowDetails }
          : p
      )
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'default'
      case 'in_progress':
        return 'secondary'
      case 'draft':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getSubmissionStatusVariant = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'submitted':
        return 'outline'
      case 'processing':
        return 'secondary'
      case 'processed':
      case 'completed':
        return 'default'
      case 'failed':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  const getTabIcon = (tab: ProfileStatusFilter) => {
    switch (tab) {
      case 'all':
        return Building2
      case 'pending':
        return Clock
      case 'processed':
        return CheckCircle
      case 'assigned':
        return UserCheck
      default:
        return Building2
    }
  }

  const getAssetIcon = (assetType: string) => {
    switch (assetType) {
      case 'pdf':
        return FileText
      case 'chart':
        return BarChart
      default:
        return FileText
    }
  }

  const isProfileProcessing = (profile: EnhancedProfile) => {
    // Show processing UI during completion animation
    if (completingProfiles.has(profile.id) || fadingOutProfiles.has(profile.id)) {
      return true
    }
    
    // Don't consider completed workflows as processing (unless in completion animation)
    if (profile.workflowProgress && 
        (profile.workflowProgress.status === 'completed' || 
         profile.workflowProgress.data.processing_metrics.progress_percentage >= 100)) {
      return false
    }
    
    const hasActiveWorkflow = profile.workflowProgress && 
                             profile.workflowProgress.data.processing_metrics.progress_percentage < 100 &&
                             profile.workflowProgress.data.processing_metrics.progress_percentage > 0
    
    return profile.submission?.processing_status === 'processing' || 
           executingOrchestrator === profile.id || 
           justExecutedProfiles.has(profile.id) ||
           hasActiveWorkflow
  }
  
  const getProgressDisplayState = (profile: EnhancedProfile) => {
    if (completingProfiles.has(profile.id)) {
      return 'completing'
    }
    if (fadingOutProfiles.has(profile.id)) {
      return 'fading'
    }
    return 'processing'
  }

  if (loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading intelligence profiles...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div className="ml-2">
              <p className="text-red-600 font-medium">Error loading profiles</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <Button 
                onClick={loadProfiles} 
                variant="outline" 
                className="mt-2"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Intelligence Profiles
              </h1>
              <p className="text-gray-600">
                Manage and execute onboarding profiles for orchestrator processing
              </p>
            </div>
            <Button 
              onClick={loadProfiles} 
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Profiles</p>
                  <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
                </div>
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">{counts.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Processed</p>
                  <p className="text-2xl font-bold text-green-600">{counts.processed}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Assigned</p>
                  <p className="text-2xl font-bold text-purple-600">{counts.assigned}</p>
                </div>
                <UserCheck className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Filter Tabs */}
        <div className="mb-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-4 lg:w-fit">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                All ({counts.total})
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending ({counts.pending})
              </TabsTrigger>
              <TabsTrigger value="processed" className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Processed ({counts.processed})
              </TabsTrigger>
              <TabsTrigger value="assigned" className="flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Assigned ({counts.assigned})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Profiles List */}
        <div className="space-y-6">
          {profiles.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                {React.createElement(getTabIcon(activeTab), { 
                  className: "h-12 w-12 mx-auto text-gray-400 mb-4" 
                })}
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No {activeTab === 'all' ? '' : activeTab} profiles found
                </h3>
                <p className="text-gray-600">
                  {activeTab === 'all' 
                    ? 'Intelligence profiles will appear here once onboarding is completed.'
                    : `No profiles with ${activeTab} status at the moment.`
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            profiles.map((profile) => (
              <Card key={profile.id} className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-100 hover:border-l-blue-300">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-3 flex items-center font-semibold">
                        <Building2 className="h-5 w-5 mr-3 text-blue-600" />
                        {profile.company_name}
                      </CardTitle>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-600">
                        <span className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-gray-400" />
                          {profile.contact_name}
                        </span>
                        <span className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {profile.email}
                        </span>
                        {profile.phone && (
                          <span className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            {profile.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <Badge variant={getStatusVariant(profile.onboarding_status)}>
                        Onboarding: {profile.onboarding_status}
                      </Badge>
                      {profile.submission && profile.submission.processing_status !== profile.onboarding_status && (
                        <Badge variant={getSubmissionStatusVariant(profile.submission.processing_status)}>
                          Processing: {profile.submission.processing_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 mt-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Industry</p>
                      <p className="font-medium">{profile.industry_name || profile.industry_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Tracking ID</p>
                      <p className="font-mono text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-md border border-blue-200">
                        {profile.tracking_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Created</p>
                      <p className="text-sm flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(profile.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Completion Score</p>
                      <p className="text-lg font-semibold text-blue-600">
                        {profile.completion_score || 0}%
                      </p>
                    </div>
                  </div>

                  {profile.annual_revenue && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Annual Revenue</p>
                        <p className="font-medium">{profile.annual_revenue}</p>
                      </div>
                      {profile.company_size && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Company Size</p>
                          <p className="font-medium">{profile.company_size}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Workflow Progress Section */}
                  {profile.workflowProgress && isProfileProcessing(profile) && (
                    <div className={`pt-4 border-t mb-4 -mx-6 px-6 py-4 rounded-lg transition-all duration-1000 ${
                      getProgressDisplayState(profile) === 'completing' 
                        ? 'border-green-100 bg-green-50/70 animate-pulse'
                        : getProgressDisplayState(profile) === 'fading'
                        ? 'border-green-100 bg-green-50/70 opacity-50 scale-95'
                        : 'border-blue-100 bg-blue-50/50'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className={`text-sm font-semibold flex items-center gap-2 transition-colors duration-500 ${
                          getProgressDisplayState(profile) === 'completing'
                            ? 'text-green-900'
                            : 'text-blue-900'
                        }`}>
                          {getProgressDisplayState(profile) === 'completing' ? (
                            <CheckCircle className="h-4 w-4 text-green-600 animate-bounce" />
                          ) : (
                            <Activity className="h-4 w-4 animate-pulse" />
                          )}
                          {getProgressDisplayState(profile) === 'completing'
                            ? 'Analysis Complete! 🎉'
                            : 'Analysis in Progress'
                          }
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleWorkflowDetails(profile.id)}
                          className={`h-auto p-1 ${
                            getProgressDisplayState(profile) === 'completing'
                              ? 'hover:bg-green-100'
                              : 'hover:bg-blue-100'
                          }`}
                        >
                          {profile.showWorkflowDetails ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      
                      <WorkflowProgress
                        progress={{
                          ...profile.workflowProgress.data.processing_metrics,
                          // Force 100% during completion animation
                          progress_percentage: getProgressDisplayState(profile) === 'completing' ? 100 : profile.workflowProgress.data.processing_metrics.progress_percentage,
                          completed_steps: getProgressDisplayState(profile) === 'completing' ? 6 : profile.workflowProgress.data.processing_metrics.completed_steps,
                          current_step: getProgressDisplayState(profile) === 'completing' ? 'workflow_complete' : profile.workflowProgress.data.processing_metrics.current_step
                        }}
                        isActive={getProgressDisplayState(profile) !== 'fading'}
                        error={profile.workflowProgress.message}
                        className="mb-3"
                      />
                      
                      {profile.showWorkflowDetails && profile.workflowProgress.data.logs.length > 0 && (
                        <WorkflowStepProgress
                          logs={profile.workflowProgress.data.logs}
                          currentStep={getProgressDisplayState(profile) === 'completing' ? 'workflow_complete' : profile.workflowProgress.data.processing_metrics.current_step}
                        />
                      )}
                    </div>
                  )}

                  {/* Assets section */}
                  {profile.submission?.assets && profile.submission.assets.length > 0 && (
                    <div className="pt-4 border-t border-green-100 bg-green-50/30 -mx-6 px-6 py-4 rounded-lg">
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          Analysis Reports Ready ({profile.submission.assets.length})
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {profile.submission.assets.map((asset) => {
                            const IconComponent = getAssetIcon(asset.asset_type)
                            return (
                              <Button
                                key={asset.id}
                                onClick={() => handleDownloadAsset(asset)}
                                size="sm"
                                variant="outline"
                                className="flex items-center justify-between text-xs bg-white hover:bg-green-50 border-green-200 hover:border-green-300 p-3 h-auto"
                              >
                                <div className="flex items-center">
                                  <IconComponent className="h-4 w-4 mr-2 text-green-600" />
                                  <span className="font-medium">
                                    {asset.asset_type.toUpperCase()} Report
                                  </span>
                                </div>
                                <Download className="h-3 w-3 text-green-600" />
                              </Button>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-600">
                      {profile.submission && (
                        <div className="space-y-1">
                          <div>Submitted: {formatDate(profile.submission.submitted_at)}</div>
                          {profile.submission.assigned_consultant && (
                            <div className="text-xs">Consultant: {profile.submission.assigned_consultant}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleExecuteOrchestrator(profile)}
                      disabled={executingOrchestrator === profile.id || isProfileProcessing(profile)}
                      size="sm"
                      className={`flex items-center transition-all duration-200 ${
                        isProfileProcessing(profile) || executingOrchestrator === profile.id
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                      }`}
                    >
                      {executingOrchestrator === profile.id ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Starting Analysis...
                        </>
                      ) : isProfileProcessing(profile) ? (
                        <>
                          <Activity className="h-4 w-4 mr-2 animate-pulse" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Settings className="h-4 w-4 mr-2" />
                          Run Analysis
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-8">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              showInfo={true}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              className="justify-center"
            />
          </div>
        )}
      </div>
    </div>
  )
}